'use client';
import { FC, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import Gate from './gate';
import { SharedSpriteIcon } from './icons';
import { cx } from './cx';
import {
  ILoginResult,
  LoginCloseType,
  SignUpParams,
  SocialNetwork,
  UserValidationReason,
  ValidationReason,
  useLoginAdapter,
} from './login-adapter';
import { LoggerService } from '../services/logger.service';

const log = LoggerService.getCategory('LoginSignupForm');

// -----------------------------------------------------------------------------
const ERROR_TEXTS = {
  userError: 'user error',
  connectionError: 'Connection error',
  captchaError: 'Invalid captcha please try again',
  login: {
    emptyEmail: 'Please provide your email',
    invalidEmail: 'Please use a valid email format',
    usernameRequired: 'Please provide your username',
    passwordInvalid: 'Invalid password',
    emailNotFound: 'Email doesn\'t exist',
    socialError: {
      Facebook: 'Facebook login error',
      Google: 'Google login error',
      Discord: 'Discord login error',
    },
  },
  signup: {
    emailExists: 'An account with this email already exists',
    passwordEmpty: 'Please provide your password',
    passwordInvalid: 'Password must be 8-64 characters',
    usernameEmpty: 'Please choose a username',
    usernameLengthInvalid: 'Username must be between 3 and 64 characters long',
    usernameTaken: 'Username already taken',
    usernameInvalid: 'Invalid username',
  },
  forgotPassword: {
    required: 'Email required',
    notFound: 'Email doesn\'t exist',
    error: 'User error',
  },
};
const kGenericFormErrorText = 'Something went wrong. Please try again.';

// -----------------------------------------------------------------------------
enum View {
  Login = 'Login',
  Signup = 'Signup',
  Suspended = 'Suspended',
}

interface ErrorState {
  isUserError: boolean;
  isConnectionError: boolean;
  isCaptchaError: boolean;
  text: string;
}

interface StringValue {
  value: string;
  isValid: boolean;
  wasValidated: boolean;
  errorText: string;
}

interface CheckboxValue {
  value: boolean;
}

interface FormValues {
  error: ErrorState;
  email: StringValue;
  username: StringValue;
  password: StringValue;
  confirmPassword: StringValue;
  emailConsent: CheckboxValue;
}

interface LoadingState {
  login: boolean;
  forgotPassword: boolean;
  restoreAccount: boolean;
  tryAgain: boolean;
}

// -----------------------------------------------------------------------------
interface Props {
  show: boolean;
  hideLogin: boolean;
  showGuestDownloadSkipLogin: boolean;
  // Which tab to open at. Defaults to the login tab; a host can start on signup
  // (e.g. the in-app modal opened via a navigate-to-section: signup request).
  // 'Signup' is applied through the same handler as the footer "Sign up" button,
  // so the switch behaves identically to the user clicking it.
  initialView?: 'Login' | 'Signup';
  handleShowVerification: () => void;
  handleClose: (loginCloseType?: LoginCloseType) => void;
  // Cross-cutting deps each host app supplies from its own hooks/config rather
  // than through the LoginAdapter (translation, analytics, legal links).
  t: (key: string, params?: Record<string, string>) => string;
  track: (name: string, extra?: Record<string, any>) => void;
  termsUrl: string;
  privacyUrl: string;
  // Optional host hook to bring the host window to the front after a successful
  // client login. Supplied by the in-app (Overwolf client) host via its native
  // overwolf-api service; unused on the web, where there's no client window.
  bringToFront?: () => void;
}

// -----------------------------------------------------------------------------
const LoginSignupForm: FC<Props> = (props) => {
  const {
    show,
    initialView,
    handleShowVerification,
    handleClose,
    hideLogin,
    showGuestDownloadSkipLogin,
    t,
    track,
    termsUrl,
    privacyUrl,
    bringToFront,
  } = props;

  const {
    currentUser,
    loginStateChangedEvent,
    getAndSetProfile,
    performOverwolfSignUp,
    performOverwolfLogin,
    performSocialLogin,
    resetPassword,
    cancelAccountDeletion,
    setToken,
    logout,
    validateUsername,
    validateEmail,
    validatePassword,
    isUnverified,
    recaptchaSiteKey,
  } = useLoginAdapter();

  const [view, setView] = useState<View>(View.Login);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    login: false,
    forgotPassword: false,
    restoreAccount: false,
    tryAgain: false,
  });
  const [_form, _setForm] = useState<FormValues>({
    email: {
      value: '',
      isValid: false,
      wasValidated: false,
      errorText: '',
    },
    username: {
      value: '',
      isValid: false,
      wasValidated: false,
      errorText: '',
    },
    password: {
      value: '',
      isValid: false,
      wasValidated: false,
      errorText: '',
    },
    confirmPassword: {
      value: '',
      isValid: false,
      wasValidated: false,
      errorText: '',
    },
    emailConsent: {
      value: false,
    },
    error: {
      isConnectionError: false,
      isUserError: false,
      isCaptchaError: false,
      text: '',
    },
  });

  const formRef = useRef(null);
  formRef.current = _form;

  const recaptchaRef = useRef<ReCAPTCHA>();

  useEffect(() => {
    if (!currentUser?.profile) {
      return;
    }

    const suspended = currentUser?.profile?.suspended;
    if (!suspended && view === View.Suspended) {
      handleClose();
    }
  }, [currentUser]);

  // ---------------------------------------------------------------------------
  const setForm = (newForm: FormValues) => {
    formRef.current = newForm;
    _setForm(newForm);
  };

  // ---------------------------------------------------------------------------
  useEffect(() => {
    setIsActive(show);
    if (!show) {
      document.removeEventListener('keypress', handleKeyPress);
      return;
    }

    if (shouldShowVerification()) {
      handleShowVerification();
      return;
    }

    document.addEventListener('keypress', handleKeyPress);
  }, [show]);

  // ---------------------------------------------------------------------------
  // Only react to login-state changes while the login form is actually shown.
  // Once we've moved on to the verification view (show === false) a late
  // success/Online event must NOT run determineNewView() and close the modal —
  // otherwise Overwolf registration closes the iframe before the user ever sees
  // the "verify your email" view. We wait for the user to become verified
  // instead. Mirrors the legacy in-app login behavior.
  useEffect(() => {
    if (!show) {
      return;
    }

    if (!loginStateChangedEvent) {
      return;
    }

    handleLoginStateChange();
  }, [loginStateChangedEvent]);

  // ---------------------------------------------------------------------------
  const shouldShowVerification = () => {
    if (!currentUser?.profile) {
      return false;
    }

    return isUnverified(currentUser);
  };

  // ---------------------------------------------------------------------------
  const handleKeyPress = (e) => {
    if (e.keyCode === 13) {
      switch (view) {
        case View.Login:
          void doOverwolfLogin(e);
          break;
        case View.Signup:
          void doSignup(e);
          break;
        default:
          break;
      }
    }
  };

  // ---------------------------------------------------------------------------
  const handleShowLogin = () => {
    setView(View.Login);
  };

  // ---------------------------------------------------------------------------
  const handleShowSignup = () => {
    setView(View.Signup);
  };

  // ---------------------------------------------------------------------------
  const handleForgotPassword = async () => {
    resetFormValidation();
    if (!_form.email.value || _form.email.value.length === 0) {
      setForm({
        ..._form,
        email: {
          ..._form.email,
          wasValidated: true,
          isValid: false,
          errorText: t(ERROR_TEXTS.forgotPassword.required),
        },
      });

      return;
    }

    setLoadingState({
      ...loadingState,
      forgotPassword: true,
    });

    const result = await resetPassword(_form.email.value);
    setLoadingState({
      ...loadingState,
      forgotPassword: false,
    });

    if (result?.success) {
      return;
    }

    const errorText =
      result.statusCode === 401
        ? t(ERROR_TEXTS.forgotPassword.notFound)
        : t(ERROR_TEXTS.forgotPassword.error);

    setForm({
      ..._form,
      email: {
        ..._form.email,
        wasValidated: true,
        isValid: false,
        errorText,
      },
    });
  };

  // ---------------------------------------------------------------------------
  const handleBackToLogin = () => {
    logout();
    setView(View.Login);
  };

  // ---------------------------------------------------------------------------
  const handleRestoreAccount = async () => {
    setLoadingState({
      ...loadingState,
      restoreAccount: true,
    });

    const result = await cancelAccountDeletion();

    setLoadingState({
      ...loadingState,
      restoreAccount: false,
    });

    log.info(`Cancel delete response ${JSON.stringify(result)}`);
  };

  // ---------------------------------------------------------------------------
  const handleResetRecaptcha = () => {
    recaptchaRef?.current?.reset();
  };

  // ---------------------------------------------------------------------------
  const resetFormValidation = () => {
    const newForm: FormValues = {
      ...formRef.current,
      error: {
        ...formRef.current.error,
        isConnectionError: false,
        isUserError: false,
        isCaptchaError: false,
        text: '',
      },
      email: {
        ...formRef.current.email,
        wasValidated: false,
      },
      username: {
        ...formRef.current.username,
        wasValidated: false,
      },
      password: {
        ...formRef.current.password,
        wasValidated: false,
      },
      confirmPassword: {
        ...formRef.current.confirmPassword,
        wasValidated: false,
      },
    };
    setForm(newForm);
  };

  // ---------------------------------------------------------------------------
  const resetForm = () => {
    const emptyVal: StringValue = {
      value: '',
      wasValidated: false,
      isValid: false,
      errorText: '',
    };

    const newForm: FormValues = {
      error: {
        isConnectionError: false,
        isUserError: false,
        isCaptchaError: false,
        text: '',
      },
      email: emptyVal,
      username: emptyVal,
      password: emptyVal,
      confirmPassword: emptyVal,
      emailConsent: { value: false },
    };

    setForm(newForm);
  };

  // ---------------------------------------------------------------------------
  const doSocialLogin = async (network: SocialNetwork) => {
    resetFormValidation();
    track('appstore_login_clicked', { network });
    const result = await performSocialLogin(network);
    if (result) {
      await handleLoginResult(result);
    }
  };

  // ---------------------------------------------------------------------------
  const doOverwolfLogin = async (event) => {
    event.preventDefault();
    track('appstore_login_clicked', {
      network: 'overwolf',
    });
    resetFormValidation();

    const isValid = await validateLoginForm(false);
    if (!isValid) {
      return;
    }

    setLoadingState({
      ...loadingState,
      login: true,
    });

    const result = await performOverwolfLogin(
      formRef.current.email.value,
      formRef.current.password.value,
    );

    await handleLoginResult(result);
  };

  // ---------------------------------------------------------------------------
  const validateFormUsername = async (validateTaken?: boolean) => {
    const username = formRef.current.username.value;
    const usernameValid = await validateUsername(username, validateTaken);

    const formUsername = {
      ...formRef.current.username,
      isValid: usernameValid.isValid,
      wasValidated: true,
    };
    if (!formUsername.isValid) {
      let errorText: string;
      switch (usernameValid.reason) {
        case UserValidationReason.Empty:
          errorText = t(ERROR_TEXTS.login.usernameRequired);
          break;
        case UserValidationReason.InvalidLength:
          errorText = t(ERROR_TEXTS.signup.usernameLengthInvalid);
          break;
        case UserValidationReason.Taken:
          errorText = t(ERROR_TEXTS.signup.usernameTaken);
          break;
        case UserValidationReason.Invalid:
        default:
          errorText = t(ERROR_TEXTS.signup.usernameInvalid);
      }

      formUsername.errorText = errorText;
    }

    return formUsername;
  };

  // ---------------------------------------------------------------------------
  const validateformEmail = () => {
    const email = formRef.current.email.value;
    const emailValid = validateEmail(email);
    const formEmail = {
      ...formRef.current.email,
      isValid: emailValid.isValid,
      wasValidated: true,
    };
    if (!emailValid.isValid) {
      formEmail.errorText =
        emailValid.reason === ValidationReason.Empty
          ? t(ERROR_TEXTS.login.emptyEmail)
          : t(ERROR_TEXTS.login.invalidEmail);
    }

    return formEmail;
  };

  // ---------------------------------------------------------------------------
  const validateformPassword = () => {
    const passwordValid = validatePassword(
      formRef.current.password.value,
      false,
    );

    const formPassword = {
      ...formRef.current.password,
      isValid: passwordValid.isValid,
      wasValidated: true,
    };

    if (!formPassword.isValid) {
      formPassword.errorText =
        passwordValid.reason === ValidationReason.InvalidLength
          ? t(ERROR_TEXTS.signup.passwordInvalid)
          : t(ERROR_TEXTS.login.passwordInvalid);
    }

    return formPassword;
  };

  // ---------------------------------------------------------------------------
  const validateFormConfirmPassword = () => {
    const passwordValid = validatePassword(
      formRef.current.confirmPassword.value,
      false,
    );

    const formPassword = {
      ...formRef.current.confirmPassword,
      isValid: passwordValid.isValid,
      wasValidated: true,
    };

    if (!formPassword.isValid) {
      formPassword.errorText = t(ERROR_TEXTS.login.passwordInvalid);
    }

    return formPassword;
  };

  // ---------------------------------------------------------------------------
  const validateLoginForm = async (checkUserName = true) => {
    const formEmail = validateformEmail();
    const formPassword = validateformPassword();
    const formConfirmPassword = validateFormConfirmPassword();

    if (!checkUserName) {
      setForm({
        ...formRef.current,
        email: formEmail,
        password: formPassword,
        // username: formUsername,
        confirmPassword: formConfirmPassword,
      });
      return formEmail.isValid && formPassword.isValid;
    }

    const formUsername = await validateFormUsername(true);

    setForm({
      ...formRef.current,
      email: formEmail,
      password: formPassword,
      username: formUsername,
      confirmPassword: formConfirmPassword,
    });

    return formUsername.isValid && formEmail.isValid && formPassword.isValid;
  };

  // ---------------------------------------------------------------------------
  const handleLoginResult = async (result: ILoginResult) => {
    setLoadingState({
      ...loadingState,
      login: false,
    });
    if (result.status === 'success') {
      setToken(result.token);
      const profile = await getAndSetProfile?.();

      if (!profile?.suspended) {
        handleClose('success');
      }

      resetForm();
      return;
    }

    if (result.error?.includes('Too Many Requests')) {
      setForm({
        ...formRef.current,
        error: {
          ...formRef.current.error,
          isUserError: false,
          isConnectionError: true,
          text: 'Too many attempts. Please wait a few minutes and try again.',
        },
      });
      return;
    }

    switch (result.error) {
      case 'AlreadyTryingToConnect':
      case 'AlreadyConnected':
        await checkSuspendedAndRetry();
        break;
      case 'AccountsServiceUnresponsive':
        setForm({
          ...formRef.current,
          error: {
            isUserError: false,
            isConnectionError: true,
            text: t(kGenericFormErrorText),
          },
        });
        break;
      case 'user-doesnt-exist':
        setForm({
          ...formRef.current,
          error: {
            ...formRef.current.error,
            isUserError: false,
            isConnectionError: false,
          },
          email: {
            ...formRef.current.email,
            isValid: false,
            wasValidated: true,
            errorText: t(ERROR_TEXTS.login.emailNotFound),
          },
        });
        break;
      case 'invalid-password':
        setForm({
          ...formRef.current,
          error: {
            ...formRef.current.error,
            isUserError: false,
            isConnectionError: false,
          },
          password: {
            ...formRef.current.password,
            isValid: false,
            wasValidated: true,
            errorText: t(ERROR_TEXTS.login.passwordInvalid),
          },
        });
        break;
      default:
        setForm({
          ...formRef.current,
          error: {
            isUserError: true,
            isConnectionError: false,
            text: t(kGenericFormErrorText),
          },
        });
        break;
    }
  };

  // ---------------------------------------------------------------------------
  const doSignup = async (event) => {
    event.preventDefault();
    resetFormValidation();

    const isValid = await validateLoginForm();
    if (!isValid) {
      return;
    }

    const formUsername = await validateFormUsername(true);
    setForm({
      ...formRef.current,
      username: formUsername,
    });

    if (!formUsername.isValid) {
      return;
    }

    if (!recaptchaRef.current) {
      return;
    }

    const recaptchaValue = recaptchaRef.current.getValue();
    const signupOptions: SignUpParams = {
      username: formRef.current.username.value,
      email: formRef.current.email.value,
      password: formRef.current.password.value,
      captchaV2Response: recaptchaValue,
      sendEmails: formRef.current.emailConsent.value,
    };

    setLoadingState({
      ...loadingState,
      login: true,
    });
    const result = await performOverwolfSignUp(signupOptions);

    track('appstore_register_submit', {
      network: 'overwolf',
      formValid: isValid,
      result: result.success ? 'success' : 'failure',
      error: result.error ? result.error : 'n/a',
    });
    await handleSignupResponse(result);
  };

  // ---------------------------------------------------------------------------
  const handleSignupResponse = async (response) => {
    const { success, error } = response;
    if (success) {
      setToken(response.token);

      setLoadingState({
        ...loadingState,
        login: false,
      });

      handleShowVerification();
      return;
    }

    setLoadingState({
      ...loadingState,
      login: false,
    });

    if (!error || error === '') {
      setForm({
        ...formRef.current,
        error: {
          ...formRef.current.error,
          isUserError: true,
          errorText: ERROR_TEXTS.userError,
        },
      });

      log.error('unexpected server response for signup', response);
      return;
    }

    switch (response.error) {
      case 'invalid captcha':
        handleResetRecaptcha();
        setForm({
          ...formRef.current,
          error: {
            ...formRef.current.error,
            isCaptchaError: true,
            errorText: ERROR_TEXTS.captchaError,
          },
        });
        break;

      case 'email exist':
        setForm({
          ...formRef.current,
          email: {
            ...formRef.current.email,
            wasValidated: true,
            isValid: false,
            errorText: ERROR_TEXTS.signup.emailExists,
          },
        });
        break;

      case 'invalid email':
        setForm({
          ...formRef.current,
          email: {
            ...formRef.current.email,
            wasValidated: true,
            isValid: false,
            errorText: ERROR_TEXTS.login.invalidEmail,
          },
        });
        break;

      case 'username exist':
        setForm({
          ...formRef.current,
          username: {
            ...formRef.current.email,
            wasValidated: true,
            isValid: false,
            errorText: ERROR_TEXTS.signup.usernameTaken,
          },
        });
        break;

      default:
        log.info('unexpected server response for signup', response);
        setForm({
          ...formRef.current,
          error: {
            ...formRef.current.error,
            isUserError: true,
            errorText: ERROR_TEXTS.userError,
          },
        });
        break;
    }
  };

  // ---------------------------------------------------------------------------
  const handleSubmit = (e) => {
    switch (view) {
      case View.Login:
        void doOverwolfLogin(e);
        break;
      case View.Signup:
        void doSignup(e);
        break;
    }
  };

  // ---------------------------------------------------------------------------
  const handleUsernameChange = (e) => {
    setForm({
      ..._form,
      username: {
        ..._form.username,
        value: e.target.value,
      },
    });
  };

  // ---------------------------------------------------------------------------
  const handleUsernameBlur = async () => {
    if (!isSignup()) {
      return;
    }

    setForm({
      ..._form,
      username: {
        ..._form.username,
        wasValidated: false,
      },
    });

    const formUsername = await validateFormUsername();
    setForm({
      ..._form,
      username: formUsername,
    });
  };

  // ---------------------------------------------------------------------------
  const handleEmailChange = (e) => {
    setForm({
      ..._form,
      email: {
        ..._form.email,
        value: e.target.value,
      },
    });
  };

  // ---------------------------------------------------------------------------
  const handleEmailBlur = () => {
    if (!isSignup()) {
      return;
    }

    setForm({
      ..._form,
      email: {
        ..._form.email,
        wasValidated: false,
      },
    });

    const formEmail = validateformEmail();
    setForm({
      ..._form,
      email: formEmail,
    });
  };

  // ---------------------------------------------------------------------------
  const handlePasswordChange = (e) => {
    setForm({
      ..._form,
      password: {
        ..._form.password,
        value: e.target.value,
      },
    });
  };

  // ---------------------------------------------------------------------------
  const handlePasswordBlur = function () {
    if (!isSignup()) {
      return;
    }

    setForm({
      ..._form,
      password: {
        ..._form.password,
        wasValidated: false,
      },
    });

    const formPassword = validateformPassword();
    setForm({
      ..._form,
      password: formPassword,
    });
  };

  // ---------------------------------------------------------------------------
  const handleConfirmPasswordChange = (e) => {
    setForm({
      ..._form,
      confirmPassword: {
        ..._form.confirmPassword,
        value: e.target.value,
      },
    });
  };

  // ---------------------------------------------------------------------------
  const handleConfirmPasswordBlur = function () {
    if (!isSignup()) {
      return;
    }

    setForm({
      ..._form,
      confirmPassword: {
        ..._form.confirmPassword,
        wasValidated: false,
      },
    });

    const formConfirmPassword = validateFormConfirmPassword();
    setForm({
      ..._form,
      confirmPassword: formConfirmPassword,
    });
  };

  // ---------------------------------------------------------------------------
  const handleEmailConsentChange = (e) => {
    setForm({
      ..._form,
      emailConsent: {
        ..._form.emailConsent,
        value: e.target.checked,
      },
    });
  };

  // ---------------------------------------------------------------------------
  const determineNewView = (HideView = false) => {
    if (!currentUser?.profile) {
      return;
    }

    if (HideView) {
      handleClose('success');
    }

    if (isUnverified(currentUser)) {
      handleShowVerification();
    }
  };

  // ---------------------------------------------------------------------------
  const handleLoginStateChange = () => {
    if (!loginStateChangedEvent) {
      return;
    }
    const { status, connectionStatus, suspended } = loginStateChangedEvent;

    setLoadingState({
      ...loadingState,
      login: status === 'success' && connectionStatus === 'Online',
      tryAgain: false,
    });

    if (status === 'success') {
      if (connectionStatus === 'Online' && !suspended) {
        setForm({
          ..._form,
          error: {
            ..._form.error,
            isConnectionError: false,
          },
        });

        bringToFront?.();

        determineNewView(hideLogin);
      } else if (suspended) {
        setView(View.Suspended);
      }

      // if (connectionState === 'Offline') {
      //   localStorage.setItem("suspendedButAskedToSeeLogin", false);
      // }

      return;
    }

    if (status === 'ConnectionError') {
      setForm({
        ..._form,
        error: {
          ..._form.error,
          isUserError: false,
          isConnectionError: true,
        },
      });
    }

    // if (error === "Wrong username/password") {
    //   setForm({
    //     ..._form,
    //     password: {
    //       ..._form.password,
    //       isValid: false
    //     }
    //   });
    // } else {
    //   setForm({
    //     ..._form,
    //     error: {
    //       ..._form.error,
    //       isConnectionError: false,
    //       isUserError: true
    //     }
    //   });
    // }
  };

  // ---------------------------------------------------------------------------
  const handleCreateAccount = () => {
    track('appstore_register_clicked', {
      network: 'overwolf',
    });
    handleShowSignup();
  };

  // ---------------------------------------------------------------------------
  // A host can ask to start on the signup tab (the in-app modal opened via a
  // navigate-to-section: signup request). Route it through handleCreateAccount
  // so it triggers exactly the same logic as clicking the footer "Sign up"
  // button (tracking included).
  useEffect(() => {
    if (initialView === 'Signup') {
      handleCreateAccount();
    }
  }, []);

  // ---------------------------------------------------------------------------
  const checkSuspendedAndRetry = async () => {
    if (currentUser?.profile?.suspended) {
      return;
    }

    log.warn('checkSuspendedAndRetry not implemented yet');
    // logout();
    // if (logoutRes.status !== 'loggedin') {
    //   await doOverwolfLogin();
    // }
  };

  // ---------------------------------------------------------------------------
  const isSignup = (): boolean => {
    return view === View.Signup;
  };

  // ---------------------------------------------------------------------------
  const isFormError = (): boolean => {
    return (
      _form.error.isUserError ||
      _form.error.isConnectionError ||
      _form.error.isCaptchaError
    );
  };

  // ---------------------------------------------------------------------------
  const isUsernameError = (): boolean => {
    return _form.username.wasValidated && !_form.username.isValid;
  };

  // ---------------------------------------------------------------------------
  const isEmailError = (): boolean => {
    return _form.email.wasValidated && !_form.email.isValid;
  };

  // ---------------------------------------------------------------------------
  const isPasswordError = (): boolean => {
    return _form.password.wasValidated && !_form.password.isValid;
  };

  // ---------------------------------------------------------------------------
  const isConfirmPasswordError = (): boolean => {
    return _form.confirmPassword.wasValidated && !_form.confirmPassword.isValid;
  };

  // ---------------------------------------------------------------------------
  const className = cx({
    'login-container': true,
    'regular-create-account': isSignup(),
    collapsed: !isActive,
  });

  // ---------------------------------------------------------------------------
  const formErrorClassName = cx({
    'validation-big-box': true,
    active: isFormError(),
  });

  const usernameValidationClassName = cx({
    'validation-tooltip': true,
    active: isUsernameError(),
  });

  const emailValidationClassName = cx({
    'validation-tooltip': true,
    active: isEmailError(),
  });

  const passwordValidationClassName = cx({
    'validation-tooltip': true,
    active: isPasswordError(),
  });

  const confirmPasswordValidationClassName = cx({
    'validation-tooltip': true,
    active: isConfirmPasswordError(),
  });

  const submitClassName = cx({
    'submit-button': true,
    'btn-primary': true,
    loading: loadingState.login,
  });

  const forgotPassowrdLoaderClasName = cx({
    'buttonless-loader': true,
    loading: loadingState.forgotPassword,
  });

  const suspendedClassName = cx({
    'account-suspended': true,
    collapsed: view !== View.Suspended,
  });

  const restoreLoaderClassName = cx({
    'buttonless-loader': true,
    loading: loadingState.restoreAccount,
  });

  // ---------------------------------------------------------------------------
  if (!show) {
    return null;
  }

  // ---------------------------------------------------------------------------
  return (
    <section className={className}>
      <div className="login-form-header">
        <div className="go-back" />
        <button
          className="clean-icon-btn close-window"
          onClick={() => handleClose('skip')}
        >
          <SharedSpriteIcon icon="closeSmall" />
        </button>
      </div>

      {!currentUser?.profile?.username && (
        <div className="login-form-container">
          <form className="login-form">
            <div className="form-title">
              <h1 className="login-title">Log in</h1>
              <h1 className="register-title">Create an account</h1>
              <h2 className="register-sub-title">
                Already have an account?&nbsp;
                <button type="button" onClick={handleShowLogin}>
                  Log in
                </button>
              </h2>
            </div>

            <div className={formErrorClassName}>
              <SharedSpriteIcon icon="validationAlert" />
              {_form.error.text || t(kGenericFormErrorText)}
            </div>

            <fieldset>
              <div className="login-register-social">
                <div className="login-services">
                  <button
                    type="button"
                    className="login-service-google"
                    onClick={() => doSocialLogin(SocialNetwork.Google)}
                  >
                    <div className="login-service-icon">
                      <SharedSpriteIcon icon="login-social-google" />
                    </div>
                    Log in with Google
                  </button>
                  {/* <button
                    type='button'
                    className="login-service-facebook"
                    onClick={() => doSocialLogin(SocialNetwork.Facebook)}
                  >
                    <div className="login-service-icon">
                      <svg>
                        <use href={`${kSpriteImagePath}#login-social-facebook`} />
                      </svg>
                    </div>
                    Log in with Facebook
                  </button> */}
                  <button
                    type="button"
                    className="login-service-discord"
                    onClick={() => doSocialLogin(SocialNetwork.Discord)}
                  >
                    <div className="login-service-icon">
                      <SharedSpriteIcon icon="login-social-discord" />
                    </div>
                    Log in with Discord
                  </button>
                  <button
                    type="button"
                    className="login-service-twitch"
                    onClick={() => doSocialLogin(SocialNetwork.Twitch)}
                  >
                    <div className="login-service-icon">
                      <SharedSpriteIcon icon="login-social-twitch" />
                    </div>
                    Log in with Twitch
                  </button>
                </div>
                <div className="or">
                  <span>Or</span>
                </div>
              </div>

              <div className="inputs-container">
                <div className="input-item username">
                  <input
                    type="text"
                    id="user-name-input"
                    placeholder=""
                    value={_form.username.value}
                    onChange={handleUsernameChange}
                    onBlur={handleUsernameBlur}
                  />
                  <label className="floating-label" htmlFor="user-name-input">
                    Username
                  </label>
                  <div
                    className={usernameValidationClassName}
                    id="usernamevalid"
                  >
                    <div className="validation-tooltip-box">
                      {_form.username.errorText}
                    </div>
                  </div>
                </div>
                <div className="input-item email">
                  <div className={forgotPassowrdLoaderClasName}>
                    <div className="loader">
                      <SharedSpriteIcon icon="button-loading-ring" />
                    </div>
                  </div>
                  <input
                    type="text"
                    id="email-input"
                    placeholder=""
                    value={_form.email.value}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                  />
                  <label className="floating-label" htmlFor="email-input">
                    Email
                  </label>
                  <div className={emailValidationClassName}>
                    <div className="validation-tooltip-box">
                      {_form.email.errorText}
                    </div>
                  </div>
                </div>
                <div className="input-item password">
                  <input
                    type="password"
                    id="password-input"
                    placeholder=""
                    value={_form.password.value}
                    onChange={handlePasswordChange}
                    onBlur={handlePasswordBlur}
                  />
                  <label className="floating-label" htmlFor="password-input">
                    Password
                  </label>
                  <div className={passwordValidationClassName}>
                    <div className="validation-tooltip-box">
                      {_form.password.errorText}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="forgot-password"
                    onClick={handleForgotPassword}
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="input-item confirm-password">
                  <input
                    type="password"
                    id="confirm-password-input"
                    placeholder=""
                    value={_form.confirmPassword.value}
                    onChange={handleConfirmPasswordChange}
                    onBlur={handleConfirmPasswordBlur}
                  />
                  <label
                    className="floating-label"
                    htmlFor="confirm-password-input"
                  >
                    Confirm Password
                  </label>
                  <div className={confirmPasswordValidationClassName}>
                    <div className="validation-tooltip-box">
                      {_form.password.errorText}
                    </div>
                  </div>
                </div>
              </div>

              {isSignup() && (
                <div id="captcha-wrapper" className="captcha-small-wrapper">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaSiteKey}
                    onExpired={handleResetRecaptcha}
                    theme="dark"
                  />
                </div>
              )}

              <div className="checboxes-container">
                <label className="cbox-consent" htmlFor="email-consent">
                  <input
                    className="checkbox"
                    type="checkbox"
                    name="emailme-consent"
                    id="email-consent"
                    checked={_form.emailConsent.value}
                    onChange={handleEmailConsentChange}
                    hidden
                  />
                  <b />
                  <span>
                    Email me about Overwolf updates, apps, and challenges for my
                    favorite games.
                  </span>
                </label>
              </div>

              <button
                type="button"
                className={submitClassName}
                onClick={handleSubmit}
              >
                <div className="loader">
                  <SharedSpriteIcon icon="button-loading-ring" />
                </div>
                <div className="submit-button-text register">
                  Create an account
                </div>
                <div className="submit-button-text login">Log in</div>
              </button>
            </fieldset>
          </form>

          <div className="form-footer">
            <h2 className="login-sub-title">
              Don’t have an account?&nbsp;
              <button type="button" onClick={handleCreateAccount}>
                Sign up
              </button>
            </h2>
          </div>

          <div className="terms-container">
            <span>
              <div className="account-terms">
                By clicking “Create an account”, you agree to Overwolf’s
              </div>
              <div className="login-terms">
                By continuing you agree to Overwolf’s
              </div>
              <a href={termsUrl} target="_blank" rel="noreferrer">
                Terms of Use
              </a>{' '}
              & &nbsp;
              <a href={privacyUrl} target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
            </span>

            {showGuestDownloadSkipLogin && (
              <button
                type="button"
                className="continue-as-a-guest"
                onClick={() => handleClose('skip')}
              >
                Continue download as a guest
              </button>
            )}
          </div>
        </div>
      )}

      <div className={suspendedClassName}>
        <h1>Account scheduled for deletion</h1>

        <p>
          This account is scheduled for deletion and is suspended until then.{' '}
          <br /> Log in to restore your account.
        </p>

        <button
          type="button"
          className="btn-primary"
          onClick={handleBackToLogin}
        >
          Back to login
        </button>

        <p className="sub-text">
          <span>I've changed my mind </span>
          <button onClick={handleRestoreAccount}>Restore my account</button>
        </p>

        <div className={restoreLoaderClassName}>
          <div className="loader">
            <SharedSpriteIcon icon="button-loading-ring" />
          </div>
        </div>
      </div>

      {/* <MiniProfileView logout={logout} handleClose={handleClose} /> */}

      <Gate />
    </section>
  );
};

export default LoginSignupForm;
